import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { toggleModal, updateMessages } from 'apps/auth/actions/appAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { changeFormField, changeFormFieldValue, scrollToField, setFieldError, updateFormProperties } from 'apps/auth/actions/formAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import PhoneBook from 'apps/auth/components/common/PhoneContacts/PhoneBook';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import Form from 'apps/auth/components/form-multi/Form';
import { getEmptyMandatoryOrFieldsWithError, hasContacts } from 'apps/auth/components/form-multi/utils';
import FromFields from 'apps/auth/components/ga/accounts/send/FromFields';
import GaSendCalculatorScreen from 'apps/auth/components/ga/accounts/send/GaSendCalculatorScreen';
import { PHONE_BOOK, PHONE_CALCULATOR } from 'apps/auth/components/ga/accounts/send/GaSendConstants';
import GAErrorDetailsPopup from 'apps/auth/components/ga/accounts/send/popups/GAErrorDetailsPopup';
import SendCommentPopup from 'apps/auth/components/ga/accounts/send/popups/SendCommentPopup/SendCommentPopup';
import ToFields from 'apps/auth/components/ga/accounts/send/ToFields';
import Disclaimer from 'apps/auth/components/send/disclaimers/Disclaimer';
import { CALCULATOR_SCREEN, RECIPIENT_MAIN_INFO_SCREEN, CARD_EXTRA_FIELDS_SCREEN } from 'apps/auth/components/send/SendConstants';
import BasePage from 'apps/auth/components/utils/BasePage';
import { DELIVERY_CASH, DELIVERY_CONTACT, DELIVERY_HOME } from 'apps/auth/constants/Constants';
import { AMOUNT_FROM, AMOUNT_TO, TO_BANK_CODE } from 'apps/auth/constants/fieldsName';
import { GA_SEND_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_GA_POCKET } from 'apps/auth/constants/UrlConstants';
import { VIETNAM } from 'apps/auth/constants/WellKnownCountries';
import { toLowerCase } from 'apps/auth/helpers/utils';
import { useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';
import { GASendMultiScreenService, hasGaExceedLimitErrors } from 'apps/auth/services/GASendMultiScreenService';

const defaultScreen = (forceShowRecipientScreen, showCalculatorFirst, deliveryMethod) => {
    if (forceShowRecipientScreen) {
        return RECIPIENT_MAIN_INFO_SCREEN;
    } else if (showCalculatorFirst) {
        if (deliveryMethod === DELIVERY_CONTACT) {
            return PHONE_CALCULATOR;
        }
        return CALCULATOR_SCREEN;
    } else if (deliveryMethod === DELIVERY_CONTACT) {
        return PHONE_BOOK;
    }
    return RECIPIENT_MAIN_INFO_SCREEN;
};

const GASendMultiScreenPage = (props) => {
    const navigate = useNavigate();
    const messages = usePageMessages();
    const fields = useFields(GA_SEND_FORM_NAME);
    const { pocket, toCountry, toCurrency, showDisclaimer, commission, deliveryMethod, isPayee, showCalculatorFirst, forceShowRecipientScreen } = useExtra();

    const [screen, setScreen] = useState(defaultScreen(forceShowRecipientScreen, showCalculatorFirst, deliveryMethod));
    const submitRef = useRef(null);
    const controllerRef = useRef(null);
    const popupRef = useRef(null);
    const [disabled, setDisabled] = useState(true);
    const clickedOnViewRatesFirst = useRef(null);
    const [regularFlow, toggleRegularFlow] = useState(true);
    const [backUrl, setBackUrl] = useState();

    const { current: gaSendMultiScreenService } = useRef(new GASendMultiScreenService(props, setScreen, setDisabled, pocket?.id));

    useEffect(() => {
        if (__isBrowser__) {
            gaSendMultiScreenService.setRefs(controllerRef, submitRef, popupRef);
            gaSendMultiScreenService.setFormActions(changeFormField, changeFormFieldValue, scrollToField, setFieldError, updateFormProperties);
            gaSendMultiScreenService.setAppActions(props.toggleModal, props.updateMessages);
        }
    }, [__isBrowser__]);

    const gaSendUrl = `${AUTH_GA_POCKET}send`;

    const getPhoneAction = () => {
        return `${AUTH_GA_POCKET}${pocket?.id}/send/${toCountry?.code.toLowerCase()}/${toCurrency?.code.toLowerCase()}/phone/${deliveryMethod}`;
    };

    const confirm = (e) => {
        e.preventDefault();
        props.toggleModal(false);
    };

    const goBack = (e) => {
        e.preventDefault();

        const isRepeat = props.stateOptions?.repeat;
        if (isRepeat) {
            window.location.href = `/auth/ga/pocket/${pocket?.id}/activity`;
        } else if (RECIPIENT_MAIN_INFO_SCREEN !== screen && PHONE_BOOK !== screen) {
            gaSendMultiScreenService.regularBackFlow(screen);
        } else {
            navigate(-1);
        }
    };

    useEffect(() => {
        const isCashPickup = deliveryMethod === DELIVERY_CASH;
        const isHomeDelivery = deliveryMethod === DELIVERY_HOME;
        const isVietnam = toCountry?.id === VIETNAM;
        if (showDisclaimer) {
            if (!isVietnam || (isVietnam && isHomeDelivery) || (isVietnam && isCashPickup && fields[TO_BANK_CODE]?.value)) {
                props.toggleModal(
                    true,
                    <Disclaimer
                        formName={GA_SEND_FORM_NAME}
                        clickAgree={confirm}
                        backLink={`${AUTH_GA_POCKET}${pocket?.id}/send/${toLowerCase(toCountry?.code)}/delivery`}
                    />,
                    true,
                    false,
                );
            }
        }
    }, [showDisclaimer, fields?.[TO_BANK_CODE]?.value]);

    useEffect(() => {
        setBackUrl(`${AUTH_GA_POCKET}${pocket?.id}/send/${toLowerCase(toCountry?.code)}/${toLowerCase(toCurrency?.code)}`);
        if (CALCULATOR_SCREEN === screen && getEmptyMandatoryOrFieldsWithError(fields).length === 0) {
            submitRef?.current?.click();
        }
    }, [screen]);

    useEffect(() => {
        if (fields) {
            gaSendMultiScreenService.toggleContinueButton(fields, screen);
        }
    }, [fields]);

    useEffect(() => {
        setScreen(defaultScreen(forceShowRecipientScreen, showCalculatorFirst, deliveryMethod));
    }, [deliveryMethod]);

    const submitHandlerWrapper = (_responseFields, json) => {
        if (regularFlow) {
            gaSendMultiScreenService.regularSubmitHandler(fields, deliveryMethod, messages, json, screen);
        } else {
            gaSendMultiScreenService.rateFirstSubmitHandler(fields, deliveryMethod, messages, json, screen);
        }
    };

    const screenValidation = (e) => {
        const isRegularFlow = regularFlow && !clickedOnViewRatesFirst.current;
        if (RECIPIENT_MAIN_INFO_SCREEN === screen && isRegularFlow) {
            e.preventDefault();
            gaSendMultiScreenService.screenValidation(fields, messages, toCountry?.highRisk, switchToNextScreen, screen);
        } else {
            switchToNextScreen();
        }
    };

    const showErrors = (errorFields) => {
        let keys = [];
        if (Array.isArray(errorFields)) {
            errorFields.forEach((f) => keys.push(f.name));
        } else {
            keys = Object.keys(errorFields);
        }
        const nextScreen = PHONE_CALCULATOR;
        if (nextScreen !== screen) {
            setScreen(nextScreen);
        }
    };

    const checkPhoneBookCalculator = (e, fields) => {
        const fieldsWithErrors = getEmptyMandatoryOrFieldsWithError(fields);
        if (fieldsWithErrors && fieldsWithErrors.length > 0) {
            e.preventDefault();
            showErrors(fieldsWithErrors);
            gaSendMultiScreenService.setErrors(fieldsWithErrors);
        } else {
            if (PHONE_CALCULATOR === screen) {
                popupRef?.current?.open(<SendCommentPopup popupRef={popupRef} formName={GA_SEND_FORM_NAME} />);
            }
        }
    };

    const viewRatesFirst = (e) => {
        e.preventDefault();
        clickedOnViewRatesFirst.current = true;
        setScreen(CALCULATOR_SCREEN);
    };

    const switchToNextScreen = (e) => {
        if (screen === CALCULATOR_SCREEN && hasGaExceedLimitErrors(fields[AMOUNT_FROM], fields[AMOUNT_TO])) {
            e.preventDefault();
            popupRef?.current?.open(
                <GAErrorDetailsPopup
                    isBonus={false}
                    maxAmount={commission?.restrictionFrom?.maxAmount}
                    multiple={commission?.restrictionFrom?.multiple}
                    currencyCode={commission?.restrictionFrom?.ccyIso}
                    currencyCodeFrom={commission?.restrictionFrom?.ccyIso}
                    isFrom={true}
                    amountField={fields[AMOUNT_FROM]}
                    popupRef={popupRef}
                    formName={GA_SEND_FORM_NAME}
                />,
            );
        } else {
            const isRegularFlow = regularFlow && !clickedOnViewRatesFirst.current;
            toggleRegularFlow(isRegularFlow);
            if (isRegularFlow) {
                gaSendMultiScreenService.setNextScreen(fields, screen);
            } else {
                gaSendMultiScreenService.setNextScreenRateFirstFlow(fields, screen);
            }
        }
    };

    const onBackIconClick = (e) => {
        if (regularFlow) {
            clickedOnViewRatesFirst.current = false;
            goBack(e);
        } else {
            gaSendMultiScreenService.viewRateFirstBackFlow(e, screen);
        }
    };
    const isShowButton = hasContacts() || screen === RECIPIENT_MAIN_INFO_SCREEN;

    return (
        <>
            <CommonPage backUrl={backUrl} hasBottomPanel onClickIcon={onBackIconClick}>
                {!!fields && (
                    <Form
                        formName={GA_SEND_FORM_NAME}
                        action={`${AUTH_GA_POCKET}${pocket?.id}/send.preCheck`}
                        getCanonicalPath={true}
                        onSubmitHandler={submitHandlerWrapper}
                    >
                        {screen === PHONE_BOOK && (
                            <PhoneBook formName={GA_SEND_FORM_NAME} setScreen={setScreen} action={getPhoneAction()} submitRef={submitRef} url={gaSendUrl} />
                        )}
                        {screen === RECIPIENT_MAIN_INFO_SCREEN && (
                            <ToFields isGA={true} formName={GA_SEND_FORM_NAME} regularFlow={regularFlow} viewRatesFirst={viewRatesFirst} isPayee={isPayee} />
                        )}
                        {screen === CARD_EXTRA_FIELDS_SCREEN && (
                            <FromFields formName={GA_SEND_FORM_NAME} checkFieldsAndSubmitFormIfHasNoErrors={switchToNextScreen} setScreen={setScreen} />
                        )}
                        {screen === CALCULATOR_SCREEN && (
                            <GaSendCalculatorScreen
                                popupRef={popupRef}
                                formName={GA_SEND_FORM_NAME}
                                checkFieldsAndSubmitFormIfHasNoErrors={switchToNextScreen}
                            />
                        )}
                        {screen === PHONE_CALCULATOR && (
                            <GaSendCalculatorScreen
                                popupRef={popupRef}
                                formName={GA_SEND_FORM_NAME}
                                checkFieldsAndSubmitFormIfHasNoErrors={checkPhoneBookCalculator}
                            />
                        )}
                        <FixedBottomPanel isRightAligned>
                            <Button
                                type="submit"
                                raised={true}
                                disabled={disabled}
                                variant={isShowButton ? 'primary' : 'hidden'}
                                ref={submitRef}
                                onClick={screenValidation}
                                caption={messages.continue}
                            />
                        </FixedBottomPanel>
                    </Form>
                )}
            </CommonPage>
            <PopupNew ref={popupRef} />
        </>
    );
};
export default connect(null, {
    scrollToField,
    setFieldError,
    changeFormField,
    changeFormFieldValue,
    updateFormProperties,
    toggleModal,
    updateMessages,
    addExtras,
})(BasePage(GASendMultiScreenPage));
